import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { formatDecimal } from "@/lib/format";
import { AlertCircle, Calculator, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
// Accordion removed to avoid Radix ref churn
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ServicePatternGrid } from "../ServicePatternGrid";
import { formatCHF, rappenToCHF, DEFAULT_CURRENCY } from "@/lib/money";
import { type PartialServicePatternEntry, type Service } from "@/types/billing";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { TariffsService } from "@/api/services/TariffsService";
import type { billing_TariffVersionResponse } from "@/api/models/billing_TariffVersionResponse";
import { useMultiGroupCostCalculation } from "@/hooks/useMultiGroupCostCalculation";
import { detectServiceConflicts } from "@/hooks/billing/validationUtils";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useCurrency } from "@/hooks/useCurrency";

interface MultiGroupServicePattern {
  [groupType: string]: Record<string, PartialServicePatternEntry[]>;
}

interface TariffVersions {
  [groupType: string]: string;
}

/**
 * Apply a tariff-version selection for a single group, ignoring spurious empty
 * values. Radix Select can emit `onValueChange("")` while its controlled value
 * hydrates or the component re-mounts; without this guard that empty string
 * clobbers the contract's stored (possibly inactive) tariff version, leaving the
 * dropdown stuck on "(No tariff available)". A real selection is always a tariff
 * id or the explicit "none" sentinel — never "". Returns the previous map
 * (same reference) when the change is a no-op so callers can skip the update.
 */
export function applyTariffVersionChange(
  prev: TariffVersions,
  groupType: string,
  tariffVersionId: string
): TariffVersions {
  if (!tariffVersionId) {
    return prev;
  }
  return { ...prev, [groupType]: tariffVersionId };
}

/**
 * Compute the tariff versions offered in a group's dropdown. Always drops
 * id-less rows and sorts ACTIVE versions first. By default only ACTIVE versions
 * plus this contract's currently-stored version are listed — historical inactive
 * versions are noise. When showArchived is true (the edit screen's "show
 * archived/old tariff versions" toggle), every non-deleted version is offered so
 * an admin can keep, re-point to, or add an ARCHIVED/EXPIRED tariff version when
 * correcting a past contract version.
 */
export function filterSelectableTariffs(
  groupTariffs: billing_TariffVersionResponse[],
  selectedTariffId: string,
  showArchived: boolean
): (billing_TariffVersionResponse & { id: string })[] {
  return groupTariffs
    .filter(
      (tariff): tariff is billing_TariffVersionResponse & { id: string } =>
        Boolean(tariff.id)
    )
    .filter(
      (tariff) =>
        showArchived ||
        (tariff.status || "").toUpperCase() === "ACTIVE" ||
        tariff.id === selectedTariffId
    )
    .slice()
    .sort((a, b) => {
      const aActive = (a.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
      const bActive = (b.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
      return aActive - bActive;
    });
}

interface ContractWizardStepScheduleProps {
  servicePattern: MultiGroupServicePattern;
  setServicePattern: (pattern: MultiGroupServicePattern) => void;
  tariffVersions: TariffVersions;
  setTariffVersions: (versions: TariffVersions) => void;
  childId: string;
  services?: Service[];
  subsidised?: boolean;
  startDate?: string;
  hideTotalCostCard?: boolean;
  showHeader?: boolean;
  /**
   * When true (the create-wizard default), an empty group is auto-populated with
   * its ACTIVE default tariff version so a brand-new contract starts with a
   * sensible selection. Set to false on the edit screen: there the stored DB
   * value (even an inactive/archived version) is authoritative and must never be
   * overwritten — auto-selecting would race with contract hydration and replace
   * the real, possibly inactive, version with a random active one.
   */
  autoSelectDefaultTariff?: boolean;
  /**
   * Edit screen only: when true, a "Show archived/old tariff versions" toggle is
   * offered so an admin correcting a past contract version can keep, re-point to,
   * or add an ARCHIVED/EXPIRED tariff version. Off by default (create-wizard
   * behaviour: ACTIVE + the stored version only).
   */
  allowArchivedTariffs?: boolean;
}

export function ContractWizardStepSchedule({
  servicePattern,
  setServicePattern,
  tariffVersions,
  setTariffVersions,
  childId,
  services = [],
  subsidised,
  startDate,
  hideTotalCostCard = false,
  showHeader = true,
  autoSelectDefaultTariff = true,
  allowArchivedTariffs = false,
}: ContractWizardStepScheduleProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const activeCurrency = currency || DEFAULT_CURRENCY;
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  // Edit screen: reveal ARCHIVED/EXPIRED tariff versions in the dropdowns so a
  // past contract version can be corrected. Off by default; ignored unless
  // allowArchivedTariffs is set.
  const [showArchivedTariffs, setShowArchivedTariffs] = useState(false);

  // All groups are shown expanded to avoid interactive ref churn

  const calculationOptions = useMemo(
    () => ({
      isSubsidised: !!subsidised,
      useEnhancedAPI: true,
      calculationDate: startDate,
      lockAgeToCalculationMonth: true,
    }),
    [subsidised, startDate]
  );

  // Fetch group type enum options (includes system + custom, localized)
  const { options: rawGroupTypeOptions, isLoading: isLoadingEnums } =
    useEnumOptions("groups", "type");

  const groupTypeOptions = useMemo(() => {
    if (!rawGroupTypeOptions || rawGroupTypeOptions.length === 0) {
      return [] as typeof rawGroupTypeOptions;
    }

    return [...rawGroupTypeOptions].sort((a, b) => {
      const isAStandard = a.value === "STANDARD";
      const isBStandard = b.value === "STANDARD";
      if (isAStandard && !isBStandard) return -1;
      if (!isAStandard && isBStandard) return 1;
      return (a.label || "").localeCompare(b.label || "");
    });
  }, [rawGroupTypeOptions]);

  // Fetch tariff versions across all statuses. Inactive ones are needed so
  // existing contracts that reference archived/expired versions can display
  // their actual stored value instead of silently falling back to the active
  // default. The backend caps limit at 100, which is well above the number of
  // tariff versions a single org accumulates in practice.
  const { data: allTariffVersions, isLoading: isLoadingTariffs } = useQuery({
    // Scope the cache by org so a tenant switch never serves another org's
    // tariff versions from a stale cache entry.
    queryKey: ["tariff-versions", "all", orgId],
    queryFn: async () => {
      const response = await TariffsService.getApiV1TariffVersions(
        undefined,    // groupType
        undefined,    // status - include all
        undefined,    // isDefault
        false,        // includeRates
        undefined,    // search
        'created_at', // sortBy
        'desc',       // sortOrder
        100,          // limit (backend max)
        0             // offset
      );
      return response?.data || [];
    },
    enabled:
      isSignedIn && isAuthReady && !!orgId && groupTypeOptions.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const formatAmount = useCallback(
    (valueInRappen: number) =>
      formatCHF(rappenToCHF(valueInRappen), true, activeCurrency),
    [activeCurrency]
  );

  // Group tariff versions by group type
  const tariffsByGroupType = useMemo(() => {
    if (!allTariffVersions)
      return new Map<string, billing_TariffVersionResponse[]>();

    const grouped = new Map<string, billing_TariffVersionResponse[]>();

    allTariffVersions.forEach((tariff) => {
      const groupType = tariff.group_type;
      if (groupType) {
        if (!grouped.has(groupType)) {
          grouped.set(groupType, []);
        }
        grouped.get(groupType)!.push(tariff);
      }
    });

    return grouped;
  }, [allTariffVersions]);

  useEffect(() => {
    // Only the create flow pre-selects an active default. On the edit screen the
    // stored tariff version (active or inactive) is authoritative, so skipping
    // this avoids clobbering it during contract hydration.
    if (!autoSelectDefaultTariff) {
      return;
    }
    if (groupTypeOptions.length === 0) {
      return;
    }

    const nextVersions: TariffVersions = { ...tariffVersions };
    let updated = false;

    groupTypeOptions.forEach((group) => {
      const groupKey = group.value || "";
      if (!groupKey || nextVersions[groupKey]) {
        return;
      }

      const tariffs = tariffsByGroupType.get(groupKey) || [];
      const activeTariffs = tariffs.filter(
        (tariff) => (tariff.status || "").toUpperCase() === "ACTIVE"
      );
      if (activeTariffs.length === 0) {
        return;
      }

      const defaultTariff = activeTariffs.find((tariff) => tariff.is_default);
      const selectedTariff = defaultTariff || activeTariffs[0];
      if (selectedTariff?.id) {
        nextVersions[groupKey] = selectedTariff.id;
        updated = true;
      }
    });

    if (updated) {
      setTariffVersions(nextVersions);
    }
  }, [
    autoSelectDefaultTariff,
    groupTypeOptions,
    tariffsByGroupType,
    tariffVersions,
    setTariffVersions,
  ]);

  // Use multi-group cost calculation hook
  const enhancedResult = useMultiGroupCostCalculation(
    servicePattern,
    tariffVersions,
    childId,
    services,
    undefined,
    calculationOptions
  );
  const totalCostBreakdown = enhancedResult.totalCostBreakdown;
  const groupCostBreakdowns = enhancedResult.groupCostBreakdowns || {};
  const warnings = enhancedResult.warnings || [];
  const { conflicts, errors } = useMemo(
    () => detectServiceConflicts(servicePattern, services, i18n),
    [servicePattern, services, i18n]
  );
  const isValidServiceSelection = useCallback(
    (day: string, serviceCode: string, targetGroupType: string) => {
      return !Object.entries(servicePattern).some(([groupType, pattern]) => {
        if (groupType === targetGroupType) return false;
        const dayServices = pattern?.[day] || [];
        return dayServices.some((svc) => svc.serviceCode === serviceCode);
      });
    },
    [servicePattern]
  );
  const display = React.useMemo(() => {
    const weekly =
      totalCostBreakdown.periods?.reduce(
        (sum, p) => sum + (p.weekly || 0),
        0
      ) || 0;
    const monthly = totalCostBreakdown.totalAmount || 0;
    const dailyCombined: Record<
      string,
      { services: string[]; amount: number }
    > = {};
    totalCostBreakdown.periods?.forEach((p) => {
      Object.entries(p.daily || {}).forEach(([day, info]) => {
        if (!dailyCombined[day])
          dailyCombined[day] = { services: [], amount: 0 };
        dailyCombined[day].amount += info.amount || 0;
        if (Array.isArray(info.services))
          dailyCombined[day].services.push(...info.services);
      });
    });
    return { weekly, monthly, daily: dailyCombined };
  }, [totalCostBreakdown]);

  // Validate service patterns across groups
  const validateServicePatterns = (
    patterns: MultiGroupServicePattern
  ): boolean => {
    return Object.values(patterns).some((pattern) =>
      Object.values(pattern).some((services) => services && services.length > 0)
    );
  };

  const handleGroupServicePatternChange = (
    groupType: string,
    pattern: Record<string, PartialServicePatternEntry[]>
  ) => {
    setServicePattern({
      ...servicePattern,
      [groupType]: pattern,
    });
  };

  const handleTariffVersionChange = (
    groupType: string,
    tariffVersionId: string
  ) => {
    const next = applyTariffVersionChange(
      tariffVersions,
      groupType,
      tariffVersionId
    );
    // applyTariffVersionChange returns the same reference for a no-op (e.g. a
    // spurious empty onValueChange), so skip the state update in that case.
    if (next !== tariffVersions) {
      setTariffVersions(next);
    }
  };

  const getDayLabel = (day: string | number) => {
    // Handle both string day names (for backward compatibility) and integer day numbers
    let dayLower: string;
    if (typeof day === 'number') {
      // Convert integer to day name (1=monday, 2=tuesday, etc.)
      const dayNames = ['', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      dayLower = dayNames[day] || '';
    } else {
      dayLower = day.toLowerCase();
    }

    const labels: Record<string, string> = {
      monday: i18n._({ id: "common.days.monday", message: "Monday" }),
      tuesday: i18n._({ id: "common.days.tuesday", message: "Tuesday" }),
      wednesday: i18n._({ id: "common.days.wednesday", message: "Wednesday" }),
      thursday: i18n._({ id: "common.days.thursday", message: "Thursday" }),
      friday: i18n._({ id: "common.days.friday", message: "Friday" }),
      saturday: i18n._({ id: "common.days.saturday", message: "Saturday" }),
      sunday: i18n._({ id: "common.days.sunday", message: "Sunday" }),
    };
    return labels[dayLower] || String(day);
  };

  if (isLoadingEnums || isLoadingTariffs) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!groupTypeOptions.length) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <Trans id="contracts.wizard.schedule.noGroupTypes">
            No group types are configured. Please configure group types in
            settings first.
          </Trans>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h3 className="text-lg font-medium">
            <Trans id="contracts.wizard.schedule.title">
              Weekly Service Schedule
            </Trans>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            <Trans id="contracts.wizard.schedule.multiGroupDescription">
              Configure service schedules for each group type. Each group can
              have its own schedule and tariff.
            </Trans>
          </p>
        </div>
      )}

      {/* Group Sections (always expanded) */}
      {allowArchivedTariffs && (
        <label className="mb-2 flex w-fit items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={showArchivedTariffs}
            onCheckedChange={(value) => setShowArchivedTariffs(value === true)}
          />
          <span>
            {i18n._({
              id: "contracts.tariffVersions.showArchived",
              message: "Show archived/old tariff versions",
            })}
          </span>
        </label>
      )}
      <div className="space-y-2">
        {groupTypeOptions.map((groupType) => {
          const groupKey = groupType.value || "";
          const groupTariffs = tariffsByGroupType.get(groupKey) || [];
          const groupPattern = servicePattern[groupKey] || {};
          const selectedTariffId = tariffVersions[groupKey] || "";
          // Honor the stored tariff version even if it's no longer ACTIVE
          // (archived/expired). Never silently substitute a different version.
          const safeSelectedTariffId =
            selectedTariffId &&
            groupTariffs.some((t) => t.id === selectedTariffId)
              ? selectedTariffId
              : "none";
          const selectableTariffs = filterSelectableTariffs(
            groupTariffs,
            selectedTariffId,
            allowArchivedTariffs && showArchivedTariffs
          );
          const hasSelectableTariffs = selectableTariffs.length > 0;
          // Resolve the selected version explicitly so the trigger label does
          // not depend on Radix deriving the text from its (lazily mounted)
          // item collection. Without this, an existing contract that opens with
          // its stored value hydrating after the options renders the placeholder
          // "(No tariff available)" until the dropdown is opened or the page is
          // hard-refreshed.
          const selectedTariff =
            safeSelectedTariffId !== "none"
              ? selectableTariffs.find((t) => t.id === safeSelectedTariffId)
              : undefined;
          const selectedTariffIsInactive =
            !!selectedTariff &&
            (selectedTariff.status || "").toUpperCase() !== "ACTIVE";

          return (
            <div key={groupKey} className="border rounded-lg">
              <div className="flex items-center justify-between gap-4 px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{groupType.label}</span>
                  {Object.keys(groupPattern).length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <Trans>{Object.keys(groupPattern).length} days</Trans>
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label
                    htmlFor={`tariff-${groupKey}`}
                    className="text-sm font-medium"
                  >
                    <Trans>Tariff Version:</Trans>
                  </label>
                  <Select
                    value={hasSelectableTariffs ? safeSelectedTariffId : undefined}
                    onValueChange={(value) =>
                      handleTariffVersionChange(groupKey, value)
                    }
                    disabled={!hasSelectableTariffs}
                  >
                    <SelectTrigger
                      id={`tariff-${groupKey}`}
                      className="w-[220px]"
                      disabled={!hasSelectableTariffs}
                    >
                      <SelectValue
                        placeholder={
                          hasSelectableTariffs
                            ? i18n._('contracts.tariffVersions.selectTariff')
                            : i18n._('contracts.tariffVersions.noTariff')
                        }
                      >
                        {selectedTariff ? (
                          <span>
                            {selectedTariff.name}
                            {selectedTariffIsInactive && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {i18n._('contracts.tariffVersions.inactive')}
                              </span>
                            )}
                          </span>
                        ) : undefined}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {!hasSelectableTariffs ? (
                        <SelectItem value="none" disabled>
                          <Trans id="contracts.tariffVersions.noActiveTariffs">
                            No active tariffs for this group type
                          </Trans>
                        </SelectItem>
                      ) : (
                        <>
                          <SelectItem value="none">
                            <Trans id="contracts.tariffVersions.noTariff">
                              (No tariff available)
                            </Trans>
                          </SelectItem>
                          {selectableTariffs.map((tariff) => {
                            const isInactive =
                              (tariff.status || "").toUpperCase() !== "ACTIVE";
                            return (
                              <SelectItem key={tariff.id} value={tariff.id}>
                                {tariff.name}
                                {tariff.is_default && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    <Trans id="contracts.tariffVersions.default">
                                      (Default)
                                    </Trans>
                                  </span>
                                )}
                                {isInactive && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    <Trans id="contracts.tariffVersions.inactive">
                                      (Inactive)
                                    </Trans>
                                  </span>
                                )}
                              </SelectItem>
                            );
                          })}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="px-6 pt-4 pb-6">
                {/* Service Pattern Grid */}
                <ServicePatternGrid
                  value={groupPattern}
                  onChange={(pattern) =>
                    handleGroupServicePatternChange(groupKey, pattern)
                  }
                  groupType={groupKey}
                  services={services}
                  disabled={!selectedTariffId || selectedTariffId === "none"}
                  conflicts={conflicts}
                  isValidServiceSelection={isValidServiceSelection}
                  globalPattern={servicePattern}
                />

                {/* Per-group cost breakdown removed to avoid redundancy */}
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation Messages */}
      {!validateServicePatterns(servicePattern) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <Trans id="contracts.wizard.validation.selectService">
              Please select at least one service in any group
            </Trans>
          </AlertDescription>
        </Alert>
      )}

      {/* Validation Errors (blocking) */}
      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div className="font-medium">
                <Trans>Configuration Errors (Must Fix):</Trans>
              </div>
              {errors.map((error, index) => (
                <div key={index} className="text-sm">
                  {error}
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Warnings for overlapping services (non-blocking) */}
      {warnings.length > 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div className="font-medium">
                <Trans>Warnings (Review Recommended):</Trans>
              </div>
              {warnings.map((warning, index) => (
                <div key={index} className="text-sm">
                  {warning}
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Total Cost Summary */}
      {!hideTotalCostCard && display.weekly > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <Trans id="contracts.wizard.cost.totalTitle">
                Total Estimated Monthly Cost
              </Trans>
            </CardTitle>
            <CardDescription>
              <Trans id="contracts.wizard.cost.multiGroupDescription">
                Combined cost across all selected group schedules
              </Trans>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Monthly Total - Featured */}
            <div className="bg-primary/10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">
                  <Trans id="contracts.wizard.cost.monthly">
                    Monthly Estimate
                  </Trans>
                </span>
                <span className="text-2xl font-bold text-primary">
                  {formatAmount(display.monthly)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {(() => {
                  // Compute per-service multipliers from service config
                  const selectedCodes = new Set<string>();
                  Object.values(servicePattern).forEach((groupPattern) => {
                    Object.values(groupPattern).forEach((daySvcs) => {
                      (daySvcs || []).forEach((svc) => {
                        const code =
                          (svc as PartialServicePatternEntry).serviceCode ||
                          (
                            svc as PartialServicePatternEntry & {
                              code?: string;
                              service_code?: string;
                            }
                          ).code ||
                          (
                            svc as PartialServicePatternEntry & {
                              code?: string;
                              service_code?: string;
                            }
                          ).service_code;
                        if (code) selectedCodes.add(code);
                      });
                    });
                  });

                  const groups: Record<string, string[]> = {};
                  Array.from(selectedCodes).forEach((code) => {
                    const s = services?.find((x) => x.code === code);
                    const wf = s?.actual_days
                      ? undefined
                      : subsidised
                      ? s?.week_factor_subsidised
                      : s?.week_factor;
                    const key =
                      wf && Number.isFinite(wf)
                        ? (wf as number).toFixed(2)
                        : undefined;
                    const label = s?.name || code;
                    if (key) {
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(label);
                    }
                  });

                  const parts = Object.entries(groups)
                    .sort((a, b) => Number(b[0]) - Number(a[0]))
                    .map(
                      ([mult, names]) =>
                        `${names.join(", ")} × ${formatDecimal(Number(mult), i18n.locale, 2)}`,
                    );

                  if (parts.length > 0) {
                    return i18n._({
                      id: "contracts.wizard.schedule.weeklyCostFactors",
                      message: "Weekly cost {factors}",
                      values: { factors: parts.join(" | ") },
                    });
                  }

                  // Fallback to average multiplier
                  const weekly = display.weekly;
                  const monthly = display.monthly;
                  const avg =
                    weekly > 0 ? formatDecimal(monthly / weekly, i18n.locale, 2) : "-";
                  return i18n._({
                    id: "contracts.wizard.schedule.weeklyCostAverage",
                    message: "Weekly cost × {weeks} weeks",
                    values: { weeks: avg },
                  });
                })()}
              </p>
              {(() => {
                // Additional line for actual-day services counts for the calculation month
                const d = startDate ? new Date(startDate) : new Date();
                const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
                const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                const dayKeyToNum: Record<string, number> = {
                  monday: 1,
                  tuesday: 2,
                  wednesday: 3,
                  thursday: 4,
                  friday: 5,
                  saturday: 6,
                  sunday: 7,
                };
                const countWeekdayInMonth = (weekday: number) => {
                  let count = 0;
                  const cur = new Date(monthStart);
                  while (cur <= monthEnd) {
                    const wd = cur.getDay() === 0 ? 7 : cur.getDay();
                    if (wd === weekday) count++;
                    cur.setDate(cur.getDate() + 1);
                  }
                  return count;
                };

                // Build service -> set of weekdays selected
                const svcDays = new Map<string, Set<number>>();
                Object.values(servicePattern).forEach((grp) => {
                  Object.entries(grp).forEach(([dayName, daySvcs]) => {
                    const wd = dayKeyToNum[dayName.toLowerCase()];
                    (daySvcs || []).forEach((svc) => {
                      const code =
                        (svc as PartialServicePatternEntry).serviceCode ||
                        (
                          svc as PartialServicePatternEntry & {
                            code?: string;
                            service_code?: string;
                          }
                        ).code ||
                        (
                          svc as PartialServicePatternEntry & {
                            code?: string;
                            service_code?: string;
                          }
                        ).service_code;
                      if (!code || !wd) return;
                      if (!svcDays.has(code))
                        svcDays.set(code, new Set<number>());
                      svcDays.get(code)!.add(wd);
                    });
                  });
                });

                const parts: string[] = [];
                svcDays.forEach((weekdays, code) => {
                  const s = services?.find((x) => x.code === code);
                  if (s?.actual_days) {
                    let total = 0;
                    weekdays.forEach(
                      (wd) => (total += countWeekdayInMonth(wd))
                    );
                    const label = s?.name || code;
                    parts.push(`${label} × ${total} days`);
                  }
                });

                if (parts.length === 0) return null;
                return (
                  <p className="text-sm text-muted-foreground mt-1">
                    {`Actual days this month: ${parts.join(
                      " | "
                    )}. Values vary month-to-month.`}
                  </p>
                );
              })()}
            </div>

            {/* Weekly Breakdown by Group */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>
                    <Trans id="contracts.wizard.cost.weeklyBreakdown">
                      Weekly Breakdown by Group
                    </Trans>
                  </span>
                  <span className="text-sm font-medium">
                    {formatAmount(display.weekly)}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(groupCostBreakdowns)
                  .filter(([_, breakdown]) => breakdown.weekly > 0)
                  .map(([groupType, breakdown]) => {
                    const groupTypeEnum = groupTypeOptions.find(
                      (g) => g.value === groupType
                    );
                    return (
                      <div
                        key={groupType}
                        className="flex justify-between items-center text-sm"
                      >
                        <span>
                          {groupTypeEnum ? groupTypeEnum.label : groupType}
                        </span>
                        <span>{formatAmount(breakdown.weekly)}</span>
                      </div>
                    );
                  })}
              </CardContent>
            </Card>

            {/* Daily Breakdown - Combined */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  <Trans>Daily Breakdown (All Groups)</Trans>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(display.daily)
                  .sort(([a], [b]) => {
                    const dayOrder = [
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                      "sunday",
                    ];
                    return (
                      dayOrder.indexOf(a.toLowerCase()) -
                      dayOrder.indexOf(b.toLowerCase())
                    );
                  })
                  .map(([day, details]) => (
                    <div
                      key={day}
                      className="flex justify-between items-start text-sm"
                    >
                      <div className="flex-1">
                        <span className="font-medium">{getDayLabel(day)}</span>
                        <div className="text-xs text-muted-foreground">
                          {details.services.join(", ")}
                        </div>
                      </div>
                      <span className="ml-2">
                        {formatAmount(details.amount)}
                      </span>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Info Note */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <Trans id="contracts.wizard.cost.multiGroupNote">
                  This is an estimate based on current tariff rates across all
                  group types. Actual costs may vary based on holidays,
                  absences, and adjustments.
                </Trans>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
